import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ParametryService } from '@pages/parametry/parametry.service';
import { FirebaseAuthProvider } from '@shared/modules/auth/providers/firebase';
import { catchError, Observable, of } from 'rxjs';
import { PricingService } from '@pages/parametry/pricing.service';
import { lowMessagesThreshold, nextCategory, planKey } from '@pages/parametry/pricing.config';
import { getAvailableMessages } from 'src/app/models/message-balance';
import { AdministrationUser } from 'src/app/models/user';

/* umbral fijo de "quedan pocos mensajes" cuando no se conoce la categoría de la cuenta (parametry/global.lowMessagesWarning, o este valor) */
const DEFAULT_LOW_MESSAGES_WARNING = 20;
/* se sugiere subir de categoría si se queda sin mensajes (o con pocos) faltando más de estos días para el próximo cobro */
const SUGERIR_SUBIR_SI_FALTAN_MAS_DE_DIAS = 7;

/*
 * 'none':  no se evalúa (no es administración, plan Free o la cuenta todavía no tiene messageBalance)
 * 'ok':    tiene mensajes
 * 'low':   quedan pocos: el porcentaje de los mensajes de su categoría (parametry/pricing.lowMessagesPercent, 15%) o menos (AF319)
 * 'empty': sin mensajes: no puede enviar desde la plataforma y el agente no responde
 */
export type MessageBalanceStatus = 'none' | 'ok' | 'low' | 'empty';

/*
 * Saldo de mensajes de la administración logueada, en vivo: escucha la cuenta y parametry/global,
 * así que se actualiza solo cuando las functions acreditan una compra o renuevan la suscripción.
 * Lo usan la topbar (contador) y el aviso de "sin mensajes" del layout.
 */
@Injectable({
    providedIn: 'root',
})
export class MessageBalanceService {
    private authProvider = inject(FirebaseAuthProvider);
    private parametryService = inject(ParametryService);

    private loggedUser = toSignal(this.authProvider.getLoggedUser() as unknown as Observable<AdministrationUser | null>);
    private parametry = toSignal(this.parametryService.getOne('global'));
    private pricingService = inject(PricingService);
    private pricing = toSignal(this.pricingService.get().pipe(catchError(() => of(null))));

    isAdministration = computed(() => this.loggedUser()?.type === 'administration');

    availableMessages = computed(() =>
        getAvailableMessages(this.loggedUser()?.messageBalance)
    );

    lowMessagesWarning = computed(() => {
        //proporcional a los mensajes de la categoría (AF319); si no se conoce, el umbral fijo de antes
        const porCategoria = lowMessagesThreshold(this.pricing(), this.loggedUser()?.subscription);
        if (porCategoria !== undefined) return porCategoria;
        const value = Number(this.parametry()?.lowMessagesWarning);
        return value >= 0 ? value : DEFAULT_LOW_MESSAGES_WARNING;
    });

    /*
     * Sugerencia de subir de categoría (AF320): si se queda sin mensajes o con pocos cuando faltan más de 7 días
     * para el próximo cobro (messageBalance.periodEnd), la categoría le quedó corta. undefined si no corresponde
     * (no está en low/empty, falta menos de una semana, no se conoce el período o ya está en la categoría más alta).
     */
    upgradeSuggestion = computed<{ categoria: string; dias: number; link: string; email: string } | undefined>(() => {
        const status = this.status();
        if (status !== 'low' && status !== 'empty') return undefined;
        const user = this.loggedUser();
        const fin: any = user?.messageBalance?.periodEnd;
        const fecha = fin?.toDate ? fin.toDate() : fin ? new Date(fin) : undefined;
        if (!fecha || isNaN(fecha.getTime())) return undefined;
        const dias = Math.ceil((fecha.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
        if (dias <= SUGERIR_SUBIR_SI_FALTAN_MAS_DE_DIAS) return undefined;
        const siguiente = nextCategory(this.pricing(), user?.subscription);
        if (!siguiente) return undefined;
        return { categoria: siguiente.name, dias, link: '/subscription/' + (user?.parentUid ?? ''), email: user?.email ?? '' };
    });

    status = computed<MessageBalanceStatus>(() => {
        const user = this.loggedUser();
        //mientras la cuenta no tenga messageBalance no se controla (transición: el saldo se carga con el primer cobro o compra)
        if (!user || user.type !== 'administration' || !user.messageBalance || !planKey(user.subscription)) {
            return 'none';
        }
        const available = this.availableMessages();
        if (available <= 0) {
            return 'empty';
        }
        if (available <= this.lowMessagesWarning()) {
            return 'low';
        }
        return 'ok';
    });
}
